import React, {useState} from 'react';
import {Avatar, Button, CircularProgress, TextField, Typography} from '@mui/material';
import {FaSteam} from 'react-icons/fa';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {useNavigate} from 'react-router-dom';
import api from '../../../api/axios';
import {setSignUpStep, signOut} from '../../../store/slices/authSlice';
import {RootState} from '../../../store';
import StageExitAction from './StageExitAction';

const SignupTradeUrlStep: React.FC = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);
  const [loadingExit, setLoadingExit] = useState(false);
  const auth = useSelector((state: RootState) => state.auth);
  const [value, setValue] = useState('');

  const STEAM_TRADE_URL_PREFIX = 'https://steamcommunity.com/tradeoffer/new/?partner=';

  // Validation: must start with prefix and contain more than just the prefix
  const isValid = value.trim().startsWith(STEAM_TRADE_URL_PREFIX) && value.length > STEAM_TRADE_URL_PREFIX.length;

  const steamTradeUrlLink = `https://steamcommunity.com/profiles/${auth.selectedSteamAccount?.steamId}/tradeoffers/privacy#trade_offer_access_url`;

  const handleSaveAndContinue = async () => {
    if (!isValid) return;

    setSaving(true);
    try {
      await api.put('/profile/linked-accounts/trade-url', {
        steamAccountId: auth.selectedSteamAccount?.id,
        tradeUrl: value
      });

      toast.success(t('signup.tradeUrl.success'));
      dispatch(setSignUpStep('DOWNLOAD_EXTENSION'));
    } catch (e: any) {
      toast.error(e?.response?.data);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenSteam = () => {
    window.open(steamTradeUrlLink, '_blank', 'noopener,noreferrer');
  };

  const handleExit = async () => {
    try {
      setLoadingExit(true);
      await api.post('/sign-out');
      dispatch(signOut());
      navigate('/');
    } catch (err) {
      toast.error(t('signOut.error'));
    } finally {
      setLoadingExit(false);
    }
  };

  return (
      <div className="mt-6 flex flex-col h-full">
        <div className="flex items-center gap-4 mb-6">
          <Avatar
              src={auth.selectedSteamAccount?.avatarUrl ?? undefined}
              className="w-14 h-14"
          />
          <div className="text-lg font-medium">
            {auth.selectedSteamAccount?.profileName}
          </div>
        </div>

        <div className="flex gap-2 items-start">
          <TextField
              label="Steam Trade-URL"
              fullWidth
              value={value}
              onChange={(e) => setValue(e.target.value)}
              disabled={saving}
              size="small"
              error={value.length > 0 && !isValid}
              helperText={value.length > 0 && !isValid ? 'Invalid Steam Trade-URL' : ''}
              slotProps={{
                input: {
                  className: 'h-11'
                }
              }}
          />

          <Button
              variant="contained"
              onClick={handleOpenSteam}
              disabled={saving}
              // flex-shrink-0 and fixed height to match TextField
              className="h-11 flex-shrink-0 flex gap-2 px-6 min-w-[160px] whitespace-nowrap shadow-none normal-case"
          >
            <FaSteam className="text-lg"/>
            Get Trade-URL
          </Button>
        </div>

        <div className="flex items-start gap-1 mt-2 text-gray-400 px-1">
          <InfoOutlinedIcon sx={{fontSize: 16, marginTop: '2px'}} className="flex-shrink-0"/>
          <Typography variant="caption" className="text-sm leading-tight font-normal">
            Sie können später in Ihren Einstellungen jederzeit weitere Steam-Accounts verknüpfen und verwalten.
          </Typography>
        </div>

        <div className="flex justify-between mt-12">
          <StageExitAction onExit={handleExit} loading={loadingExit}/>

          <Button
              variant="contained"
              onClick={handleSaveAndContinue}
              // Disabled if saving or value is not valid
              disabled={!isValid || saving}
              className="min-w-[150px] h-11 relative shadow-none"
          >
          <span className={saving ? 'invisible' : ''}>
            {t('common.continue')}
          </span>
            {saving && (
                <CircularProgress
                    size={24}
                    color="inherit"
                    className="absolute left-1/2 top-1/2 -ml-[12px] -mt-[12px]"
                />
            )}
          </Button>
        </div>
      </div>
  );
};

export default SignupTradeUrlStep;